import { supabase } from '../supabase/supabaseClient';
import { sanitizeLogObject, sanitizeLogString } from './logSanitizer';

/**
 * Security logging and monitoring utilities
 *
 * This module provides secure logging functionality that stores security events
 * in the audit_schema.security_logs table. All functions automatically sanitize
 * input data to prevent sensitive information from being logged.
 *
 * Note: This module works with the audit schema migration (96_move_security_logs_to_audit_schema.sql)
 * which moves security logs from public.security_logs to audit_schema.security_logs.
 */

/**
 * Severity levels for security logs
 */
export type SecurityLogSeverity = 'info' | 'warning' | 'error';

/**
 * Security log entry
 */
export interface SecurityLog {
  id: string;
  event_type: string;
  details: Record<string, unknown>;
  severity: SecurityLogSeverity;
  user_id?: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

/**
 * Security log filter options
 */
export interface SecurityLogFilter {
  event_type?: string;
  severity?: SecurityLogSeverity;
  user_id?: string;
  start_date?: Date;
  end_date?: Date;
  limit?: number;
  offset?: number;
}

/**
 * Security log statistics
 */
export interface SecurityLogStats {
  event_type: string;
  severity: SecurityLogSeverity;
  count: number;
}

/**
 * Logs a security event
 * 
 * @param eventType The type of security event
 * @param details Details about the event
 * @param severity The severity of the event
 * @param userId Optional user ID associated with the event
 * @returns The ID of the created log entry, or null if logging failed
 */
export async function logSecurityEvent(
  eventType: string,
  details: Record<string, unknown> = {},
  severity: SecurityLogSeverity = 'info',
  userId?: string
): Promise<string | null> {
  try {
    // Sanitize sensitive data before logging
    const sanitizedDetails = sanitizeLogObject(details);
    const sanitizedUserId = userId ? sanitizeLogString(userId) : null;
    
    const { data, error } = await supabase.rpc('log_security_event', {
      p_event_type: eventType,
      p_details: sanitizedDetails,
      p_severity: severity,
      p_user_id: sanitizedUserId || null
    });
    
    if (error) {
      console.error('Error logging security event:', sanitizeLogString(error.message));
      return null;
    }
    
    return data;
  } catch (err) {
    console.error('Exception in logSecurityEvent:', sanitizeLogObject(err));
    return null;
  }
}

/**
 * Gets security logs with filtering
 * 
 * @param filter Filter options
 * @returns Array of security logs
 */
export async function getSecurityLogs(
  filter: SecurityLogFilter = {}
): Promise<SecurityLog[]> {
  try {
    // Sanitize user ID if present in filter
    const sanitizedUserId = filter.user_id ? sanitizeLogString(filter.user_id) : null;
    
    const { data, error } = await supabase.rpc('get_security_logs', {
      p_event_type: filter.event_type || null,
      p_severity: filter.severity || null,
      p_user_id: sanitizedUserId || null,
      p_start_date: filter.start_date?.toISOString() || null,
      p_end_date: filter.end_date?.toISOString() || null,
      p_limit: filter.limit || 100,
      p_offset: filter.offset || 0
    });
    
    if (error) {
      console.error('Error getting security logs:', sanitizeLogString(error.message));
      return [];
    }
    
    // No need to sanitize returned logs as they're already sanitized when stored
    return data || [];
  } catch (err) {
    console.error('Exception in getSecurityLogs:', sanitizeLogObject(err));
    return [];
  }
}

/**
 * Gets security log statistics
 * 
 * @param startDate Start date for statistics (default: 30 days ago)
 * @param endDate End date for statistics (default: now)
 * @returns Array of security log statistics
 */
export async function getSecurityLogStats(
  startDate?: Date,
  endDate?: Date
): Promise<SecurityLogStats[]> {
  try {
    const { data, error } = await supabase.rpc('get_security_log_stats', {
      p_start_date: startDate?.toISOString() || null,
      p_end_date: endDate?.toISOString() || null
    });
    
    if (error) {
      console.error('Error getting security log stats:', sanitizeLogString(error.message));
      return [];
    }
    
    return data || [];
  } catch (err) {
    console.error('Exception in getSecurityLogStats:', sanitizeLogObject(err));
    return [];
  }
}

/**
 * Security monitoring service
 * Provides methods for logging and monitoring security events
 */
export const securityMonitoring = {
  /**
   * Logs a security event
   */
  logEvent: logSecurityEvent,
  
  /**
   * Gets security logs with filtering
   */
  getLogs: getSecurityLogs,
  
  /**
   * Gets security log statistics
   */
  getStats: getSecurityLogStats,
  
  /**
   * Sanitizes log data to remove sensitive information
   */
  sanitizeLogData: sanitizeLogObject,
  
  /**
   * Sanitizes log messages to remove sensitive information
   */
  sanitizeLogMessage: sanitizeLogString,
  
  /**
   * Predefined event types for common security events
   */
  eventTypes: {
    LOGIN_SUCCESS: 'login_success',
    LOGIN_FAILURE: 'login_failure',
    LOGOUT: 'logout',
    PASSWORD_CHANGE: 'password_change',
    PASSWORD_RESET: 'password_reset',
    USER_CREATED: 'user_created',
    USER_UPDATED: 'user_updated',
    USER_DELETED: 'user_deleted',
    USER_INVITED: 'user_invited',
    ROLE_ASSIGNED: 'role_assigned',
    ROLE_REVOKED: 'role_revoked',
    PERMISSION_DENIED: 'permission_denied',
    SQL_INJECTION_ATTEMPT: 'sql_injection_attempt',
    XSS_ATTEMPT: 'xss_attempt',
    CSRF_ATTEMPT: 'csrf_attempt',
    RATE_LIMIT_EXCEEDED: 'rate_limit_exceeded',
    API_ACCESS: 'api_access',
    SENSITIVE_DATA_ACCESS: 'sensitive_data_access',
    CONFIGURATION_CHANGE: 'configuration_change',
    SECURITY_SETTING_CHANGE: 'security_setting_change',
    LOG_SANITIZATION_FAILURE: 'log_sanitization_failure'
  }
};